import { forwardRef, useImperativeHandle, useState, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { InputText } from "primereact/inputtext";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { useQueryClient } from "react-query";
import contractsAPI from "../../../../services/contracts";

const AddSiblingsModal = forwardRef((props, ref) => {
    const { schoolId, trainingId, contractId } = props;
    const accessToken = "";

    const [active, setActive] = useState(false);
    const [callbackFn, setCallbackFn] = useState(() => {});
    const [selectedSiblings, setSelectedSiblings] = useState([]);
    const [searchInputValue, setSearchInputValue] = useState("");
    const [isSearchLoading, setIsSearchLoading] = useState(false);

    const toast = useRef();
    const queryClient = useQueryClient();
    
    // Get family students not yet added to the contract
    const siblingsQuery = contractsAPI.useListNonAddedStudents(schoolId, trainingId, accessToken, contractId);
    const availableSiblings = siblingsQuery?.data?.data || [];
    // Filter siblings based on search input
    const filteredSiblings = availableSiblings.filter(sibling => {
        if (!searchInputValue) return true;
        const searchTerm = searchInputValue.toLowerCase();
        return (
            sibling.firstname?.toLowerCase().includes(searchTerm) ||
            sibling.lastname?.toLowerCase().includes(searchTerm) ||
            sibling.member_id?.toLowerCase().includes(searchTerm)
        );
    });
    
    const handleSearch = () => {
        setIsSearchLoading(true);
        // Simulate search delay
        setTimeout(() => {
            setIsSearchLoading(false);
        }, 300);
    };
    
    const handleClear = () => {
        setSearchInputValue("");
        setSelectedSiblings([]);
    };
    
    const handleAddSiblings = async () => {
        if (selectedSiblings.length === 0) {
            toast.current.show({
                severity: "warn",
                summary: "Attention",
                detail: "Veuillez sélectionner au moins un membre de la famille à ajouter",
                life: 3000,
            });
            return;
        }
        
        try {
            // Extract only the student IDs from the selectedSiblings array
            const siblingIdsToAdd = selectedSiblings.map(sibling => sibling.id);
            
            // Call the service to add siblings to the contract
            await contractsAPI.UpdateContractStudents(
                schoolId,
                trainingId,
                accessToken,
                contractId,
                {
                    action: "ADD",
                    students_ids: siblingIdsToAdd
                }
            );
            
            // Clear selection and close modal
            setSelectedSiblings([]);
            setActive(false);
            
            // Show success message
            toast.current.show({
                severity: "success",
                summary: "Succès",
                detail: `${selectedSiblings.length} membre(s) de la famille ajouté(s) au contrat avec succès!`,
                life: 3000,
            });
            
            // Invalidate the query cache to refresh the available siblings list
            queryClient.invalidateQueries(["contracts", "non-added-students", schoolId, trainingId, contractId]);
            
            // Call the callback function to refresh the contract data
            callbackFn();
            
        } catch (error) {
            console.error("Error adding siblings:", error);
            toast.current.show({
                severity: "error",
                summary: "Erreur",
                detail: "Erreur lors de l'ajout des membres de la famille au contrat",
                life: 3000,
            });
        }
    };
    
    useImperativeHandle(ref, () => ({
        show: (callback) => {
            setCallbackFn(() => callback);
            setActive(true);
            siblingsQuery.refetch();
        },
    }));
    
    const footerContent = (
        <div className="flex justify-content-end gap-2">
            <Button
                label="Annuler"
                icon="pi pi-times"
                onClick={() => setActive(false)}
                className="p-button-text"
            />
            <Button
                label={`Ajouter ${selectedSiblings.length} membre(s) de la famille`}
                icon="pi pi-plus"
                onClick={handleAddSiblings}
                disabled={selectedSiblings.length === 0}
                severity="success"
            />
        </div>
    );
    
    return (
        <>
            <Toast ref={toast} />
            
            <Dialog
                header="Ajouter des frères/sœurs au contrat"
                visible={active}
                style={{ width: "80vw" }}
                breakpoints={{ "960px": "75vw", "641px": "100vw" }}
                onHide={() => setActive(false)}
                footer={footerContent}
            >
                <div className="mb-4">
                    <p className="text-700">
                        Sélectionnez un ou plusieurs membres de la famille à ajouter au contrat. 
                        Seuls les membres de la famille qui ne sont pas déjà dans le contrat sont affichés.
                    </p>
                </div>
                
                {availableSiblings.length === 0 ? (
                    <div className="text-center p-4">
                        <p className="text-500">
                            Aucun membre de la famille disponible à ajouter au contrat.
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="flex justify-content-between align-items-center mb-3">
                            <div className="flex align-items-center gap-2">
                                <IconField iconPosition="left">
                                    <InputIcon className="pi pi-search" />
                                    <InputText
                                        value={searchInputValue}
                                        onChange={(e) => setSearchInputValue(e.target.value)}
                                        placeholder="Rechercher un membre de la famille..."
                                        className="w-20rem"
                                    />
                                </IconField>
                                <Button
                                    onClick={handleSearch}
                                    icon={isSearchLoading ? "pi pi-spinner pi-spin" : "pi pi-search"}
                                    disabled={isSearchLoading}
                                    loading={isSearchLoading}
                                />
                                {searchInputValue && (
                                    <Button
                                        onClick={handleClear}
                                        icon="pi pi-times"
                                        outlined
                                        disabled={isSearchLoading}
                                    />
                                )}
                            </div>
                            
                            {selectedSiblings.length > 0 && (
                                <div className="text-500">
                                    {selectedSiblings.length} membre(s) de la famille sélectionné(s)
                                </div>
                            )}
                        </div>
                        
                        <DataTable
                            value={filteredSiblings}
                            selection={selectedSiblings}
                            onSelectionChange={(e) => setSelectedSiblings(e.value)}
                            dataKey="id"
                            showGridlines
                            className="p-datatable-sm"
                            emptyMessage="Aucun membre de la famille trouvé"
                        >
                            <Column
                                selectionMode="multiple"
                                headerStyle={{ width: "3rem" }}
                            />
                            <Column
                                field="firstname"
                                header="Prénom"
                            />
                            <Column
                                field="lastname"
                                header="Nom"
                            />
                            <Column
                                field="member_id"
                                header="Immatriculation"
                            />
                            <Column
                                field="classe.name"
                                header="Classe"
                            />
                            <Column
                                field="level.name"
                                header="Niveau"
                            />
                        </DataTable>
                    </>
                )}
            </Dialog>
        </>
    );
});

export default AddSiblingsModal;

